import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getOrder, ordersQueryKeys } from '../../api/orders';
import { eventsQueryKeys, getOrderEvents } from '../../api/events';
import {
  ORDERS_SEARCH_DEFAULTS,
  parseOrdersSearch,
} from '../../hooks/orders/useOrdersRouterSearch';
import {
  ORDER_HISTORY_SEARCH_DEFAULTS,
  parseOrderHistorySearch,
} from '../../hooks/orders/useOrderHistoryRouterSearch';
import {
  EVENT_HISTORY_SEARCH_DEFAULTS,
  parseEventHistorySearch,
} from '../../hooks/events/useEventHistoryRouterSearch';
import { OrdersPageSkeleton } from '../../components/Skeleton/OrdersPageSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditOrderDetailsSkeleton } from '../../components/Skeleton/EditOrderDetailsSkeleton';
import { HistorySkeleton } from '../../components/Skeleton/HistorySkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type OrderManagement from '../../components/OrderManagement/index';
import type OrderDetails from '../../components/OrderDetails/index';
import type EditOrderDetails from '../../components/EditOrderDetails/index';
import type OrderHistory from '../../components/OrderHistory/index';
import type EventHistory from '../../components/EventHistory/index';
import type { Order } from '../../api/orders';
import type { ApiEventSummary } from '../../api/events';

export const ORDER_STALE_TIME_MS = 30_000;

// Shared query for /orders/$id/{details,edit}. Mirrors `useResaleItemRaw` /
// `useProductRaw` — the consumer `useOrder` returns the raw Order shape so we
// can reuse it directly.
export function useOrderRaw(idNum: number, seed: Order | undefined) {
  return useQuery({
    queryKey: ordersQueryKeys.detail(idNum),
    queryFn: () => getOrder(idNum),
    staleTime: ORDER_STALE_TIME_MS,
    ...(seed ? { initialData: seed, initialDataUpdatedAt: Date.now() } : {}),
  });
}

function useEventSummary(
  orderIdNum: number,
  idNum: number,
  seed: ApiEventSummary | undefined,
): ApiEventSummary | undefined {
  const { data } = useQuery({
    queryKey: eventsQueryKeys.orderEvents(orderIdNum),
    queryFn: () => getOrderEvents(orderIdNum),
    staleTime: ORDER_STALE_TIME_MS,
    enabled: !seed,
  });

  return seed ?? data?.find((event) => event.id === idNum);
}

export const ordersIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  validateSearch: parseOrdersSearch,
  search: { middlewares: [stripSearchParams(ORDERS_SEARCH_DEFAULTS)] },
  component: function OrdersIndexRoute() {
    const { orderSalespersons, orderAllCount, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.orders'));
    const [Component, setComponent] = useState<typeof OrderManagement | null>(null);
    useEffect(() => {
      import('../../components/OrderManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <OrdersPageSkeleton label={t('common.loading')} />;
    }
    return (
      <Component
        salespersons={orderSalespersons}
        allOrdersCount={orderAllCount}
        currentUser={currentUser}
      />
    );
  },
});

export const orderDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$id/details',
  component: function OrderDetailsRoute() {
    const params = orderDetailsRoute.useParams();
    const { initialOrder, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialOrder && initialOrder.id === idNum ? initialOrder : undefined;
    const { data } = useOrderRaw(idNum, seed);
    // `undefined` keeps the product prefix; `null` would request a bare title.
    useDocumentTitle(
      data?.reference_number ?? t('common.page_titles.orders'),
      data?.customer_display_name ?? undefined,
    );
    const [Component, setComponent] = useState<typeof OrderDetails | null>(null);
    useEffect(() => {
      import('../../components/OrderDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <DetailsSkeleton label={t('common.loading')} />;
    }
    return <Component orderId={idNum} initialOrder={data} currentUser={currentUser} />;
  },
});

export const orderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$id/edit',
  component: function OrderEditRoute() {
    const params = orderEditRoute.useParams();
    const { initialOrder, orderSalespersons, orderSources, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialOrder && initialOrder.id === idNum ? initialOrder : undefined;
    const { data } = useOrderRaw(idNum, seed);
    useDocumentTitle(
      data ? t('common.page_titles.edit', { name: data.reference_number }) : t('common.page_titles.orders'),
    );
    const [Component, setComponent] = useState<typeof EditOrderDetails | null>(null);
    useEffect(() => {
      import('../../components/EditOrderDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <EditOrderDetailsSkeleton label={t('common.loading')} />;
    }
    return (
      <Component
        orderId={idNum}
        orderData={data}
        salespersons={orderSalespersons ?? []}
        orderSources={orderSources ?? []}
        currentUser={currentUser}
      />
    );
  },
});

export const orderHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$id/history',
  validateSearch: parseOrderHistorySearch,
  search: { middlewares: [stripSearchParams(ORDER_HISTORY_SEARCH_DEFAULTS)] },
  component: function OrderHistoryRoute() {
    const params = orderHistoryRoute.useParams();
    const { initialOrder } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialOrder && initialOrder.id === idNum ? initialOrder : undefined;
    const { data } = useOrderRaw(idNum, seed);
    useDocumentTitle(
      data?.reference_number ?? t('common.page_titles.orders'),
      data ? t('order_history.heading') : undefined,
    );
    const [Component, setComponent] = useState<typeof OrderHistory | null>(null);
    useEffect(() => {
      import('../../components/OrderHistory/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <HistorySkeleton label={t('common.loading')} />;
    }
    return <Component orderId={idNum} orderNumber={data.reference_number} />;
  },
});

export const eventHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$orderId/events/$id/history',
  validateSearch: parseEventHistorySearch,
  search: { middlewares: [stripSearchParams(EVENT_HISTORY_SEARCH_DEFAULTS)] },
  component: function EventHistoryRoute() {
    const params = eventHistoryRoute.useParams();
    const { initialOrder, initialEvent } = useAppRouterInitial();
    const { t } = useTranslation();
    const orderIdNum = Number(params.orderId);
    const idNum = Number(params.id);
    const orderSeed = initialOrder && initialOrder.id === orderIdNum ? initialOrder : undefined;
    const eventSeed = initialEvent && initialEvent.id === idNum ? initialEvent : undefined;
    const { data: order } = useOrderRaw(orderIdNum, orderSeed);
    const event = useEventSummary(orderIdNum, idNum, eventSeed);
    useDocumentTitle(
      event?.event_id ?? t('common.page_titles.orders'),
      event ? t('event_history.heading') : undefined,
    );
    const [Component, setComponent] = useState<typeof EventHistory | null>(null);
    useEffect(() => {
      import('../../components/EventHistory/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !order || !event) {
      return <HistorySkeleton label={t('common.loading')} />;
    }
    return (
      <Component
        orderId={orderIdNum}
        orderNumber={order.reference_number}
        eventId={idNum}
        eventNumber={event.event_id}
      />
    );
  },
});
